import React, { createContext, ReactNode, useContext, useEffect, useMemo, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { logAuthLogout, logAuthSuccess, useSplunkLogger } from '@resolutionlife/ui';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: { name: string; email?: string; sub: string };
  login: () => void;
  logout: () => Promise<void>;
  getAccessToken: () => string | undefined;
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const oktaAuthResult = useOktaAuth();
  const authState = oktaAuthResult?.authState;
  const oktaAuth = oktaAuthResult?.oktaAuth;
  const splunkLogger = useSplunkLogger();

  const isAuthenticated = authState?.isAuthenticated || false;
  const isLoading: boolean = !authState || !!authState.isPending;

  const user = {
    name: authState?.idToken?.claims?.name || 
          authState?.idToken?.claims?.preferred_username || 
          authState?.idToken?.claims?.email || 'User',
    email: authState?.idToken?.claims?.email,
    sub: authState?.idToken?.claims?.sub || '',
  };

  // Log auth success when user becomes authenticated
  const hasLoggedAuth = useRef(false);
  useEffect(() => {
    if (isAuthenticated && user.sub && !hasLoggedAuth.current) {
      hasLoggedAuth.current = true;
      logAuthSuccess(splunkLogger, { provider: 'Okta', userId: user.sub });
    }
    if (!isAuthenticated) {
      hasLoggedAuth.current = false;
    }
  }, [isAuthenticated, user.sub, splunkLogger]);

  const login = () => { oktaAuth?.signInWithRedirect(); };
  const logout = async () => {
    logAuthLogout(splunkLogger, { provider: 'Okta' });
    await oktaAuth?.signOut();
  };
  const getAccessToken = () => oktaAuth?.getAccessToken();
  
  const fetchWithAuth = async (url: string, options: RequestInit = {}, retryCount = 0): Promise<Response> => {
    const maxRetries = 1;
    
    try {
      let parsed: URL | null = null;
      try {
        parsed = new URL(url);
      } catch (parseErr) {
        console.error('Failed to parse url for DPoP proof', parseErr);
      }
      const urlForDpop = parsed !== null ? `${parsed.origin}${parsed.pathname}` : url;
      const method = options.method ?? "GET";

      const authHeaders = await oktaAuth?.getDPoPAuthorizationHeaders({ method, url: urlForDpop });
      const headers = {
        ...options.headers,
        ...authHeaders,
      };

      const response = await fetch(url, { ...options, headers });
      
      // Handle 401 with automatic retry after token renewal
      if (response.status === 401 && retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchWithAuth(url, options, retryCount + 1);
      }
      
      return response;
    } catch (error) {
      if (retryCount < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1000));
        return fetchWithAuth(url, options, retryCount + 1);
      }
      throw error;
    }
  };

  const contextValue = useMemo(() => ({
    isAuthenticated,
    isLoading,
    user,
    login,
    logout,
    getAccessToken,
    fetchWithAuth,
  }), [isAuthenticated, isLoading, user, login, logout, getAccessToken, fetchWithAuth]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};